import React, { useEffect, useState } from "react";
import { CHAT_COLUMN_CLASS } from "../constants/chatLayout";

/**
 * Self-animating loading state for deep research.
 *
 * sonar-deep-research streams nothing for 3-6 minutes (only SSE keepalives), so this
 * card drives its own animation client-side: an elapsed timer, rotating status lines,
 * and an indeterminate progress bar — giving the user continuous feedback that the
 * report is still being generated even while the connection is silent.
 */
const STAGES = [
  "Searching the web for relevant sources…",
  "Reading and analyzing results…",
  "Cross-referencing competitors and trends…",
  "Synthesizing findings…",
  "Compiling your detailed report…",
  "Formatting the document…",
];

const formatElapsed = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

interface DeepResearchLoadingCardProps {
  /** Epoch ms the research started. When provided (e.g. on reload), the timer and
   *  stage are seeded from real elapsed time instead of restarting at zero. */
  startedAt?: number;
}

export const DeepResearchLoadingCard: React.FC<DeepResearchLoadingCardProps> = ({ startedAt }) => {
  const initialElapsed = startedAt
    ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000))
    : 0;
  const [elapsed, setElapsed] = useState(initialElapsed);
  const [stage, setStage] = useState(
    Math.min(Math.floor(initialElapsed / 8), STAGES.length - 1)
  );

  useEffect(() => {
    const tick = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    // Advance through the stages, holding on the last one until the report arrives.
    const rotate = setInterval(
      () => setStage((s) => Math.min(s + 1, STAGES.length - 1)),
      8000
    );
    return () => clearInterval(rotate);
  }, []);

  return (
    <div className={`${CHAT_COLUMN_CLASS} animate-fadeIn`}>
      <style>{`
        @keyframes deepResearchSlide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
      <div className="w-full min-w-0">
        <div className="rounded-2xl border border-theme-border bg-theme-surface-ui p-4 space-y-3">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="relative h-9 w-9 shrink-0">
              <div className="absolute inset-0 animate-spin rounded-full border-2 border-theme-accent/30 border-t-theme-accent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="h-4 w-4 text-theme-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-theme-fg leading-snug">
                Generating your research report…
              </p>
              <p className="text-xs text-theme-fg-muted mt-0.5 transition-opacity duration-500">
                {STAGES[stage]}
              </p>
            </div>
            <span className="shrink-0 font-mono text-xs tabular-nums text-theme-fg-muted">
              {formatElapsed(elapsed)}
            </span>
          </div>

          {/* Indeterminate progress bar */}
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-theme-border/60">
            <div
              className="h-full w-1/4 rounded-full bg-theme-accent"
              style={{ animation: "deepResearchSlide 1.8s ease-in-out infinite" }}
            />
          </div>

          <p className="text-xs text-theme-fg-muted">
            This usually takes 3–6 minutes. You can navigate away — your report will be
            ready when you return to this conversation.
          </p>
        </div>
      </div>
    </div>
  );
};
